import { useState } from 'react';
import { Button } from '../../components/Button/Button';
import { Card } from '../../components/Card/Card';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useUiStore } from '../../store/ui';
import {
  useCreateFamily,
  useDeleteFamily,
  useFamilies,
  useUpdateFamily,
} from '../../store/queryHooks';
import { fmtN } from '../../lib/format';
import { FamilyModal } from './FamilyModal';
import type { FamilyRow, FamilyStatus } from '@budgetx/shared';
import styles from './FamilierPage.module.css';

const STATUS_META: Record<FamilyStatus, { label: string; color: string; bg: string }> = {
  active: { label: 'Aktiv', color: 'var(--accent-dark)', bg: 'var(--accent-soft)' },
  suspended: { label: 'Deaktiveret', color: 'var(--danger)', bg: 'var(--danger-soft)' },
  invited: { label: 'Afventer adgangskode', color: '#9a6b00', bg: '#f7edd6' },
};

function familyInitial(name: string): string {
  const stripped = name.replace(/^Familie\s+/i, '').trim();
  return (stripped[0] || name.trim()[0] || '?').toUpperCase();
}

function pwLink(token: string | null): string {
  if (!token) return '';
  return `${window.location.origin}/opret-adgangskode?konto=${token}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return 'Aldrig';
  const d = new Date(iso.length <= 19 ? iso + 'Z' : iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatLastActive(iso: string | null): string {
  if (!iso) return 'Aldrig';
  const d = new Date(iso.length <= 19 ? iso + 'Z' : iso);
  if (isNaN(d.getTime())) return '—';
  const daysAgo = Math.floor((Date.now() - d.getTime()) / (24 * 3600 * 1000));
  if (daysAgo <= 0) return 'I dag';
  if (daysAgo === 1) return 'I går';
  if (daysAgo < 30) return `${daysAgo} dage siden`;
  return d.toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function FamilierPage() {
  const setToast = useUiStore((s) => s.setToast);
  const families = useFamilies();
  const createFamily = useCreateFamily();
  const updateFamily = useUpdateFamily();
  const deleteFamily = useDeleteFamily();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ id: number; name: string } | null>(null);

  if (families.isPending) return <div>Indlæser…</div>;
  if (families.isError || !families.data) return <div>Kunne ikke hente familier.</div>;

  const rows = families.data;
  const active = rows.filter((f) => f.status === 'active').length;
  const suspended = rows.filter((f) => f.status === 'suspended').length;
  const editingFamily = editingId !== null ? (rows.find((f) => f.id === editingId) ?? null) : null;

  function openCreate() {
    setEditingId(null);
    setModalOpen(true);
  }

  function openEdit(id: number) {
    setEditingId(id);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
  }

  async function onSave(input: { name: string; email: string }) {
    try {
      if (editingId !== null) {
        await updateFamily.mutateAsync({ id: editingId, ...input });
        setToast('Familie opdateret');
        closeModal();
      } else {
        const created = await createFamily.mutateAsync(input);
        if (created) {
          setEditingId(created.id);
          setToast('Familie oprettet — send adgangskode-linket');
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Handlingen fejlede';
      setToast(msg);
    }
  }

  async function toggleStatus(f: FamilyRow) {
    if (f.status === 'invited') return;
    const next: FamilyStatus = f.status === 'active' ? 'suspended' : 'active';
    try {
      await updateFamily.mutateAsync({ id: f.id, status: next });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Kunne ikke ændre status';
      setToast(msg);
    }
  }

  function copyLink(token: string | null) {
    if (!token) return;
    try {
      void navigator.clipboard.writeText(pwLink(token));
    } catch {
      /* clipboard may be blocked */
    }
    setToast('Link kopieret');
  }

  function requestDelete(f: FamilyRow) {
    setConfirmDelete({ id: f.id, name: f.name });
  }

  async function performDelete() {
    if (!confirmDelete) return;
    try {
      await deleteFamily.mutateAsync(confirmDelete.id);
      setToast('Familie slettet');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Kunne ikke slette familie';
      setToast(msg);
    } finally {
      setConfirmDelete(null);
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.kpiRow}>
        <Card compact className={styles.kpi}>
          <div className={styles.kpiLabel}>Familier i alt</div>
          <div className={styles.kpiValue}>{fmtN(rows.length)}</div>
        </Card>
        <Card compact className={styles.kpi}>
          <div className={styles.kpiLabel}>Aktive</div>
          <div className={`${styles.kpiValue} ${styles.kpiValueOk}`}>{fmtN(active)}</div>
        </Card>
        <Card compact className={styles.kpi}>
          <div className={styles.kpiLabel}>Deaktiverede</div>
          <div className={`${styles.kpiValue} ${styles.kpiValueOver}`}>{fmtN(suspended)}</div>
        </Card>
      </div>

      <div className={styles.notice}>
        <span className={styles.noticeIcon}>🔑</span>
        <div className={styles.noticeBody}>
          Din konto er <strong>admin</strong>. Hver familie er én bruger med eget budget. Flere
          logins pr. familie kommer senere — de deler samme tal.
        </div>
      </div>

      <Card>
        <div className={styles.tableHead}>
          <h2 className={styles.title}>Familier</h2>
          <Button onClick={openCreate}>+ Opret familie</Button>
        </div>
        {rows.length === 0 ? (
          <div className={styles.empty}>
            Ingen familier endnu. Klik <strong>+ Opret familie</strong> for at oprette den første.
          </div>
        ) : (
          <div className={styles.scroll}>
            <div className={styles.inner}>
              <div className={styles.headRow}>
                <div>Familie</div>
                <div>Status</div>
                <div>Oprettet</div>
                <div>Seneste aktivitet</div>
                <div className={styles.actionsHead}>Handlinger</div>
              </div>
              {rows.map((f) => {
                const sm = STATUS_META[f.status];
                const isInvited = f.status === 'invited';
                return (
                  <div key={f.id} className={styles.row}>
                    <div className={styles.familyCell}>
                      <span className={styles.avatar}>{familyInitial(f.name)}</span>
                      <div className={styles.familyName}>
                        <div className={styles.familyPrimary}>{f.name}</div>
                        <div className={styles.familySecondary}>{f.email}</div>
                      </div>
                    </div>
                    <div>
                      <span
                        className={styles.statusPill}
                        style={{ color: sm.color, background: sm.bg }}
                      >
                        <span className={styles.statusDot} style={{ background: sm.color }} />
                        {sm.label}
                      </span>
                    </div>
                    <div className={styles.dateCell}>{formatDate(f.created_at)}</div>
                    <div className={styles.dateCell}>{formatLastActive(f.last_active_at)}</div>
                    <div className={styles.actions}>
                      <button
                        type="button"
                        className={styles.iconBtn}
                        onClick={() => openEdit(f.id)}
                        title="Rediger"
                        aria-label="Rediger"
                      >
                        ✎
                      </button>
                      {isInvited && (
                        <button
                          type="button"
                          className={styles.iconBtn}
                          onClick={() => copyLink(f.invite_token)}
                          title="Kopiér adgangskode-link"
                          aria-label="Kopiér adgangskode-link"
                        >
                          🔗
                        </button>
                      )}
                      {!isInvited && (
                        <button
                          type="button"
                          className={styles.iconBtn}
                          onClick={() => toggleStatus(f)}
                          title={f.status === 'active' ? 'Deaktivér' : 'Aktivér'}
                          aria-label={f.status === 'active' ? 'Deaktivér' : 'Aktivér'}
                        >
                          {f.status === 'active' ? '⏸' : '▶'}
                        </button>
                      )}
                      <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                        onClick={() => requestDelete(f)}
                        title="Slet familie"
                        aria-label="Slet familie"
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      {modalOpen && (
        <FamilyModal
          editing={editingFamily}
          pwLink={pwLink(editingFamily?.invite_token ?? null)}
          onClose={closeModal}
          onSave={onSave}
          onCopyLink={() => copyLink(editingFamily?.invite_token ?? null)}
          onOpenLink={() => {
            if (editingFamily?.invite_token) {
              window.open(pwLink(editingFamily.invite_token), '_blank', 'noopener,noreferrer');
            }
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Slet familie"
          message={`Slet familien "${confirmDelete.name}"? Alle deres budget-data slettes også, og handlingen kan ikke fortrydes.`}
          danger
          onConfirm={performDelete}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
